import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { Cohort } from '@fatigue/shared';
import { invitePatient, listMyCohorts } from '../data/api';
import { InviteDialog } from './InviteDialog';

/**
 * "Invite a patient", above the roster.
 *
 * Sits with the caseload rather than in the administration area, because it is
 * a clinical act taken by whoever runs the cohort — the owner cannot do it at
 * all unless they are assigned. It appears only when the signed-in person has
 * a cohort to invite into, so the button is never present-but-refused.
 *
 * When they run more than one cohort, the cohort is chosen explicitly. There is
 * no default, because enrolling somebody in the wrong course is not a mistake
 * they will notice quickly.
 */
export function InvitePatient(): JSX.Element | null {
  const [cohorts, setCohorts] = useState<readonly Cohort[]>([]);
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listMyCohorts()
      .then((loaded) => {
        if (cancelled) return;
        setCohorts(loaded);
        if (loaded.length === 1) setCohortId(loaded[0]?.cohortId ?? null);
      })
      .catch(() => {
        // The roster's own error state already covers an unreachable API;
        // a second message about it here would be noise.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (cohorts.length === 0) return null;

  const chosen = cohortId ?? cohorts[0]?.cohortId ?? null;
  const cohortName = cohorts.find((c) => c.cohortId === chosen)?.name ?? '';

  return (
    <div className="sidebar__invite">
      {cohorts.length > 1 && (
        <label className="sidebar__cohort">
          <span className="visually-hidden">Cohort to invite into</span>
          <select
            value={chosen ?? ''}
            onChange={(event) => setCohortId(event.target.value)}
          >
            {cohorts.map((cohort) => (
              <option key={cohort.cohortId} value={cohort.cohortId}>
                {cohort.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <button type="button" className="ghost ghost--wide" onClick={() => setOpen(true)}>
        Invite a patient
      </button>

      {sent !== null && (
        <p className="sidebar__sent" role="status">
          Invitation sent to {sent}. They appear here once they accept it.
        </p>
      )}

      <InviteDialog
        open={open}
        title={`Invite a patient to ${cohortName}`}
        preamble="They will be emailed a link to set a password on their phone. Nothing is recorded against their name until they accept — that acceptance is their consent."
        fields={[
          { name: 'displayName', label: 'Name' },
          {
            name: 'email',
            label: 'Email address',
            type: 'email',
            hint: 'Read this back to them. A typo invites a stranger.',
          },
          { name: 'referenceCode', label: 'Reference', hint: 'e.g. MW-0412' },
        ]}
        submitLabel="Send invitation"
        onSubmit={async (values) => {
          if (chosen === null) return;
          await invitePatient(chosen, {
            email: values['email'] ?? '',
            displayName: values['displayName'] ?? '',
            referenceCode: values['referenceCode'] ?? '',
          });
          setSent(values['email'] ?? '');
        }}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
